import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import { t } from '@/lib/i18n';

const control =
  'block rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm placeholder:text-slate-400 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20 disabled:bg-slate-100';

export function Field({
  label,
  error,
  hint,
  children,
  className = '',
}: {
  label: string;
  error?: string;
  hint?: ReactNode;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function Input({
  className = '',
  invalid,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return (
    <input
      className={`${control} h-9 ${/\bw-/.test(className) ? '' : 'w-full'} ${invalid ? 'border-red-400' : ''} ${className}`}
      {...rest}
    />
  );
}

export function Textarea({
  className = '',
  invalid,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      className={`${control} w-full min-h-20 ${invalid ? 'border-red-400' : ''} ${className}`}
      {...rest}
    />
  );
}

export function Select({
  className = '',
  invalid,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select
      className={`${control} h-9 ${/\bw-/.test(className) ? '' : 'w-full'} ${invalid ? 'border-red-400' : ''} ${className}`}
      {...rest}
    />
  );
}

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-5 w-9 rounded-full transition-colors ${checked ? 'bg-brand-600' : 'bg-slate-300'}`}
      >
        <span
          className={`absolute top-0.5 left-0 size-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4.5' : 'translate-x-0.5'}`}
        />
      </button>
      {label}
    </label>
  );
}

export function ColorInput({
  value,
  onChange,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-12 cursor-pointer rounded border border-slate-300 bg-white p-1"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={invalid}
        className="w-32 font-mono"
      />
    </div>
  );
}

/** Editable list of short values (e-mails, departure times). */
export function TagInput({
  values,
  onChange,
  placeholder,
  validate,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  validate?: (v: string) => boolean;
}) {
  const add = (raw: string) => {
    const items = raw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const next = [...values];
    for (const item of items) if (!next.includes(item) && (!validate || validate(item))) next.push(item);
    onChange(next);
  };
  return (
    <div className="rounded-md border border-slate-300 bg-white p-1.5 shadow-sm focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-600/20">
      <div className="flex flex-wrap gap-1">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-sm">
            {v}
            <button
              type="button"
              className="text-slate-400 hover:text-red-600"
              onClick={() => onChange(values.filter((x) => x !== v))}
              aria-label={t('Remove {value}', { value: v })}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="min-w-32 flex-1 px-1 py-0.5 text-sm outline-none"
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(e.currentTarget.value);
              e.currentTarget.value = '';
            } else if (e.key === 'Backspace' && !e.currentTarget.value && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={(e) => {
            if (e.currentTarget.value) add(e.currentTarget.value);
            e.currentTarget.value = '';
          }}
          onPaste={(e) => {
            e.preventDefault();
            add(e.clipboardData.getData('text'));
          }}
        />
      </div>
    </div>
  );
}
